/**
 * useDeleteOperations Hook
 * 
 * Custom React hook for handling delete operations on office employee salary and bonus data.
 * Provides delete functionality with loading, error, and success state management.
 * 
 * Features:
 * - Delete by month (salary) or period (bonus)
 * - Delete by year (all records for a year)
 * - Delete by employee code (all records for an employee)
 * - Delete individual records
 * - Confirmation dialogs before deletion
 * - Automatic data refresh after successful deletion
 * - Loading, error, and success message management
 * 
 * Used by:
 * - DataDisplay component for office employee data display
 * 
 * @module useDeleteOperations
 */

import { useState } from 'react';
import { DataType } from '../types/DataDisplayTypes';
import fileUploadService from '../../../../../services/FileUploadService';
import { buildOfficeSelectionLabel } from '../utils/deleteSelectionLabel';

/**
 * useDeleteOperations Hook
 * 
 * Custom hook for handling delete operations on office employee data.
 * Manages delete operations through fileUploadService with confirmation dialogs.
 * 
 * @param dataType - Current data type ('salary' or 'bonus')
 * @param onRefreshData - Callback function to refresh data after successful deletion
 * @returns Object containing delete state and handler functions
 * 
 * @example
 * ```typescript
 * const {
 *   deleteLoading,
 *   deleteError,
 *   deleteSuccess,
 *   handleDeleteMonthData,
 *   handleDeleteYearData,
 *   handleDeleteEmployeeData,
 *   handleDeletePeriodData,
 *   handleDeleteIndividualRecord,
 *   clearDeleteMessages
 * } = useDeleteOperations('salary', () => fetchFilteredData());
 * ```
 */
export const useDeleteOperations = (dataType: DataType, onRefreshData: () => void) => {
  /**
   * State management:
   * - deleteLoading: Loading state for delete operations
   * - deleteError: Error message if delete fails (null if no error)
   * - deleteSuccess: Success message after delete (null if no success)
   */
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState<string | null>(null);

  /**
   * Handles deletion of all data for a specific month (salary data only).
   * 
   * Validates that both year and month are provided, shows confirmation dialog,
   * then deletes all records for that month and year.
   * 
   * @param filterYear - Year string to filter deletion
   * @param filterMonth - Month string to filter deletion
   * 
   * @example
   * ```typescript
   * await handleDeleteMonthData('2024', '1'); // Delete all January 2024 data
   * ```
   */
  const handleDeleteMonthData = async (filterYear: string, filterMonth: string) => {
    /**
     * Validate that both year and month are provided.
     */
    if (!filterYear || !filterMonth) {
      setDeleteError('年と月を選択してください。');
      return;
    }

    /**
     * Show confirmation dialog with warning that operation cannot be undone.
     */
    const confirmMessage = `${filterYear}年${filterMonth}月のデータを削除しますか？この操作は取り消せません。`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setDeleteLoading(true);
      setDeleteError(null);
      setDeleteSuccess(null);

      /**
       * Call fileUploadService to delete salary data for the specified year and month.
       * Only used for salary data type.
       */
      const result = await fileUploadService.deleteSalaryData(
        parseInt(filterYear), 
        parseInt(filterMonth)
      );

      if (result.success) {
        /**
         * Set success message with deleted count and refresh data.
         */
        setDeleteSuccess(`${filterYear}年${filterMonth}月のデータを${result.deletedCount}件削除しました。`);
        // Refresh the data
        onRefreshData();
      } else {
        setDeleteError(result.message || '削除に失敗しました。');
      }
    } catch (err: any) {
      console.error('Error deleting data:', err);
      setDeleteError(err.message || '削除に失敗しました。');
    } finally {
      setDeleteLoading(false);
    }
  };

  /**
   * Handles deletion of all data for a specific year.
   * 
   * Validates that year is provided, shows confirmation dialog,
   * then deletes all records for that year (both salary and bonus if applicable).
   * Uses different API methods based on dataType.
   * 
   * @param filterYear - Year string to filter deletion
   * 
   * @example
   * ```typescript
   * await handleDeleteYearData('2024'); // Delete all 2024 data
   * ```
   */
  const handleDeleteYearData = async (filterYear: string) => {
    /**
     * Validate that year is provided.
     */
    if (!filterYear) {
      setDeleteError('年を選択してください。');
      return;
    }

    /**
     * Show confirmation dialog with warning that operation cannot be undone.
     */
    const confirmMessage = `${filterYear}年のすべてのデータを削除しますか？この操作は取り消せません。`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setDeleteLoading(true);
      setDeleteError(null);
      setDeleteSuccess(null);

      /**
       * Call appropriate delete method based on data type.
       * - Salary: deleteSalaryData(year)
       * - Bonus: deleteBonusData(year) - deletes all periods for that year
       */
      let result;
      if (dataType === 'salary') {
        result = await fileUploadService.deleteSalaryData(parseInt(filterYear));
      } else {
        /**
         * For bonus data, delete all periods for the year.
         * Note: deleteBonusData(year) deletes all bonus records for that year,
         * including all periods.
         */
        result = await fileUploadService.deleteBonusData(parseInt(filterYear));
      }

      if (result.success) {
        /**
         * Set success message with deleted count and refresh data.
         */
        setDeleteSuccess(`${filterYear}年のデータを${result.deletedCount}件削除しました。`);
        // Refresh the data
        onRefreshData();
      } else {
        setDeleteError(result.message || '削除に失敗しました。');
      }
    } catch (err: any) {
      console.error('Error deleting data:', err);
      setDeleteError(err.message || '削除に失敗しました。');
    } finally {
      setDeleteLoading(false);
    }
  };

  /**
   * Deletes data matching the current dropdown selection (year + month/期 required).
   */
  const handleDeleteSelection = async (
    filterYear: string,
    filterMonth: string,
    filterPeriod: string,
    filterEmployeeCode: string
  ) => {
    if (dataType === 'salary') {
      if (!filterYear || !filterMonth) {
        setDeleteError('年と月を選択してください。');
        return;
      }
    } else {
      if (!filterYear || !filterPeriod) {
        setDeleteError('年と期を選択してください。');
        return;
      }
    }

    const label = buildOfficeSelectionLabel(
      dataType,
      filterYear,
      filterMonth,
      filterPeriod,
      filterEmployeeCode
    );
    const confirmMessage = `選択中の条件（${label}）のデータを削除しますか？この操作は取り消せません。`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setDeleteLoading(true);
      setDeleteError(null);
      setDeleteSuccess(null);

      let result;
      if (dataType === 'salary') {
        result = await fileUploadService.deleteSalaryData(
          parseInt(filterYear),
          parseInt(filterMonth),
          filterEmployeeCode || undefined
        );
      } else {
        result = await fileUploadService.deleteBonusData(
          parseInt(filterYear),
          filterPeriod,
          filterEmployeeCode || undefined
        );
      }

      if (result.success) {
        setDeleteSuccess(`選択条件（${label}）のデータを${result.deletedCount}件削除しました。`);
        onRefreshData();
      } else {
        setDeleteError(result.message || '削除に失敗しました。');
      }
    } catch (err: any) {
      console.error('Error deleting selection:', err);
      setDeleteError(err.message || '削除に失敗しました。');
    } finally {
      setDeleteLoading(false);
    }
  };

  /**
   * Handles deletion of all data for a specific period (bonus data only).
   * 
   * Validates that both year and period are provided, shows confirmation dialog,
   * then deletes all records for that period and year.
   * Only used when dataType is 'bonus'.
   * 
   * @param filterYear - Year string to filter deletion
   * @param filterPeriod - Period string to filter deletion
   * 
   * @example
   * ```typescript
   * await handleDeletePeriodData('2024', 'summer'); // Delete all summer 2024 bonus data
   * ```
   */
  const handleDeletePeriodData = async (filterYear: string, filterPeriod: string) => {
    /**
     * Validate that both year and period are provided.
     */
    if (!filterYear || !filterPeriod) {
      setDeleteError('年と期を選択してください。');
      return;
    }

    /**
     * Show confirmation dialog with warning that operation cannot be undone.
     */
    const confirmMessage = `${filterYear}年${filterPeriod}のデータを削除しますか？この操作は取り消せません。`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setDeleteLoading(true);
      setDeleteError(null);
      setDeleteSuccess(null);

      /**
       * Call fileUploadService to delete bonus data for the specified year and period.
       * Only used for bonus data type.
       */
      const result = await fileUploadService.deleteBonusData(
        parseInt(filterYear), 
        filterPeriod
      );

      if (result.success) {
        /**
         * Set success message with deleted count and refresh data.
         */
        setDeleteSuccess(`${filterYear}年${filterPeriod}のデータを${result.deletedCount}件削除しました。`);
        // Refresh the data
        onRefreshData();
      } else {
        setDeleteError(result.message || '削除に失敗しました。');
      }
    } catch (err: any) {
      console.error('Error deleting data:', err);
      setDeleteError(err.message || '削除に失敗しました。');
    } finally {
      setDeleteLoading(false);
    }
  };

  /**
   * Handles deletion of an individual record.
   * 
   * Deletes a single record for a specific employee, year, and month/period.
   * Shows confirmation dialog with employee details before deletion.
   * Uses different API methods based on dataType.
   * 
   * @param employeeCode - Employee code of the record to delete
   * @param year - Year of the record to delete
   * @param month - Month of the record to delete (for salary, ignored for bonus)
   * @param employeeName - Employee name for confirmation message
   * @param period - Optional period of the record to delete (for bonus only)
   * 
   * @example
   * ```typescript
   * // Delete salary record
   * await handleDeleteIndividualRecord('EMP001', 2024, 1, 'John Doe');
   * 
   * // Delete bonus record
   * await handleDeleteIndividualRecord('EMP001', 2024, 0, 'John Doe', 'summer');
   * ```
   */
  const handleDeleteIndividualRecord = async (employeeCode: string, year: number, month: number, employeeName: string, period?: string) => {
    if (dataType === 'salary') {
      /**
       * Salary data deletion - uses year, month, and employee code.
       */
      const confirmMessage = `${employeeName} (${employeeCode}) の${year}年${month}月のデータを削除しますか？この操作は取り消せません。`;
      if (!window.confirm(confirmMessage)) {
        return;
      }

      try {
        setDeleteLoading(true);
        setDeleteError(null);
        setDeleteSuccess(null);

        /**
         * Delete salary record using year, month, and employee code.
         */
        const result = await fileUploadService.deleteSalaryData(year, month, employeeCode);

        if (result.success) {
          /**
           * Set success message with employee details and refresh data.
           */
          setDeleteSuccess(`${employeeName} (${employeeCode}) の${year}年${month}月のデータを削除しました。`);
          // Refresh the data
          onRefreshData();
        } else {
          setDeleteError(result.message || '削除に失敗しました。');
        }
      } catch (err: any) {
        console.error('Error deleting individual salary record:', err);
        setDeleteError(err.message || '削除に失敗しました。');
      } finally {
        setDeleteLoading(false);
      }
    } else {
      /**
       * Bonus data deletion - uses year, period, and employee code.
       */
      const confirmMessage = `${employeeName} (${employeeCode}) の${year}年${period || '賞与'}のデータを削除しますか？この操作は取り消せません。`;
      if (!window.confirm(confirmMessage)) {
        return;
      }

      try {
        setDeleteLoading(true);
        setDeleteError(null);
        setDeleteSuccess(null);

        /**
         * Delete bonus record using year, period, and employee code.
         * Uses empty string if period is not provided.
         */
        const result = await fileUploadService.deleteIndividualBonusRecord(year, period || '', employeeCode);

        if (result.success) {
          /**
           * Set success message with employee details and refresh data.
           */
          setDeleteSuccess(`${employeeName} (${employeeCode}) の${year}年${period || '賞与'}のデータを削除しました。`);
          // Refresh the data
          onRefreshData();
        } else {
          setDeleteError(result.message || '削除に失敗しました。');
        }
      } catch (err: any) {
        console.error('Error deleting individual bonus record:', err);
        setDeleteError(err.message || '削除に失敗しました。');
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  /**
   * Clears delete operation messages (error and success).
   * 
   * Resets both deleteError and deleteSuccess to null.
   * Used to dismiss messages in the UI.
   */
  const clearDeleteMessages = () => {
    setDeleteError(null);
    setDeleteSuccess(null);
  };

  /**
   * Return hook values and functions.
   * 
   * Provides:
   * - deleteLoading: Loading state for delete operations
   * - deleteError: Error message (null if no error)
   * - deleteSuccess: Success message (null if no success)
   * - handleDeleteMonthData: Function to delete month data
   * - handleDeleteYearData: Function to delete year data
   * - handleDeleteEmployeeData: Function to delete employee data
   * - handleDeletePeriodData: Function to delete period data (bonus)
   * - handleDeleteIndividualRecord: Function to delete individual record
   * - clearDeleteMessages: Function to clear messages
   */
  return {
    deleteLoading,
    deleteError,
    deleteSuccess,
    handleDeleteMonthData,
    handleDeleteYearData,
    handleDeletePeriodData,
    handleDeleteSelection,
    handleDeleteIndividualRecord,
    clearDeleteMessages
  };
}; 